import { Box, Text } from '@chakra-ui/react'
import uniqBy from 'lodash/uniqBy'
import type { HTMLProps, MutableRefObject } from 'react'
import { useMemo } from 'react'
import { memo, useCallback } from 'react'
import { useEffect, useState, forwardRef } from 'react'

import { deriveType } from '../helpers'

import useGetObjects from '@app/hooks/useGetObjects'
import QueryExistingBasicCardFields from '@app/pages/strategies/components/addCards/queryExisting/queryExistingBasicCardFields'
import QueryExistingEntityFields from '@app/pages/strategies/components/addCards/queryExisting/queryExistingEntityFields'
import QueryExistingMetricFields from '@app/pages/strategies/components/addCards/queryExisting/queryExistingMetricFields'
import MapDrawerQueryForm from '@app/pages/strategies/components/drawer/mapDrawerQueryForm'
import LabelsAutocomplete from '@app/shared/autocomplete/labels/labelsAutocomplete'
import { withForwardRefAwait } from '@app/shared/withAwait'
import type { Integration, MetricSource } from '@graphql/types'

type Props = HTMLProps<HTMLFormElement> & {
  type: string
  cardTypeId?: string | null
}

type SelectLabel = {
  label: string
  value: string
}

type Label = {
  id: string
  name: string
}

const stopProp = (e) => e.stopPropagation()

const getOptionValue = (option) => option.label

const QueryExistingCardForm = forwardRef<HTMLFormElement, Props>(({ type, cardTypeId = null, ...formProps }, ref) => {
  const [typeValue, setTypeValue] = useState(() => deriveType(type))
  const [labels, setLabels] = useState<Label[]>([])

  const onLabelsChange = useCallback((value: SelectLabel[]) => {
    const newLabels = value.map((newLabel) => ({ id: newLabel.value, name: newLabel.label }))

    setLabels(newLabels)

    return null
  }, [])

  const unsortedMetricSources = useGetObjects('metricSource') as Required<
    Pick<MetricSource, 'classType' | 'sourceName'>
  >[]
  const metricSources = useMemo(() => uniqBy(unsortedMetricSources, 'sourceName'), [unsortedMetricSources])
  const unsortedEntitySources = useGetObjects('integration') as Required<
    Pick<Integration, 'classType' | 'sourceName' | 'entityTypes'>
  >[]
  const entitySources = useMemo(() => uniqBy(unsortedEntitySources, 'sourceName'), [unsortedEntitySources])

  useEffect(() => {
    setTypeValue(deriveType(type))

    // Reset labels when type changes
    setLabels([])
  }, [type])

  useEffect(() => {
    const formRef = ref as MutableRefObject<HTMLFormElement>
    // this submit is done here, because the new value is not yet set in the form in the onChange callback
    formRef.current?.requestSubmit()
  }, [ref, labels])

  const customFields = useMemo(() => {
    switch (typeValue) {
      case 'metric':
        return <QueryExistingMetricFields sources={metricSources} type={type} />
      case 'entity':
        return <QueryExistingEntityFields sources={entitySources} type={type} />
      case 'basicCard':
        return <QueryExistingBasicCardFields cardTypeId={cardTypeId} />
      default:
        return null
    }
  }, [typeValue, metricSources, entitySources, type, cardTypeId])

  // NOTE: LabelsAutocomplete is wrapped, because its onChange callback does not receive the event,
  //       but we need to stopPropagation to avoid the main form from being submitted when typing in tags.

  return (
    <MapDrawerQueryForm ref={ref} data-testid="add-existing-form" {...formProps}>
      <Text color="subtle" fontSize="sm">
        Select cards from the library
      </Text>
      {customFields}
      <Box onChange={stopProp}>
        <LabelsAutocomplete
          name="labels"
          placeholder="Filter by label"
          getOptionValue={getOptionValue}
          onChange={onLabelsChange}
          defaultValue={labels}
        />
      </Box>
    </MapDrawerQueryForm>
  )
})

QueryExistingCardForm.displayName = 'QueryExistingCardForm'

export default memo(withForwardRefAwait<HTMLFormElement, Props>(QueryExistingCardForm, 'integrationInfo'))
